import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form3921DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 3921 due dates for Tax Year 2025 | TaxZerone"
          description="Learn the Form 3921 filing deadline for Tax Year 2025. Avoid penalties by reporting ISO transactions on time with help from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-3921-due-dates/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form 3921</span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report your stock option transactions on time to stay compliant with
          IRS regulations.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3921duedates`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-3921-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 3921 due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        Form {'3921 '}
                        {
                          'must be filed by corporations that transfer stock through an ISO plan and must be '
                        }
                        furnished
                        {' to employees and '}
                        submitted
                        {' to the IRS within the required deadlines.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Employee copies are due by '}
                        <strong style={{ fontWeight: 600 }}>
                          {'January '}
                        </strong>
                        <strong style={{ fontWeight: 600 }}>31</strong>,
                        {' paper filing with the IRS is due by '}
                        <strong style={{ fontWeight: 600 }}>
                          February
                        </strong>{' '}
                        <strong style={{ fontWeight: 600 }}>28</strong>
                        {', and electronic filing is due by '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>.
                      </span>
                    </li>
                    <li>
                      <span>
                        {
                          'Late or incorrect Form 3921 filings may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the timing and circumstances of the
                        failure.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your business issued stock to an employee through an
                  Incentive Stock Option (ISO) plan, you’re required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file IRS Form 3921
                  </Link>
                  . This form reports the exercise of ISOs and must be submitted
                  by the IRS deadline to avoid penalties. Staying on top of
                  these due dates ensures smooth tax reporting and full
                  compliance.{' '}
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 3921?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#dates" sx={{ color: '#0202ff' }}>
                          Important dates to file Form 3921?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pen" sx={{ color: '#0202ff' }}>
                          Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#filing"
                          id="whatis"
                          sx={{ color: '#0202ff' }}
                        >
                          Advantages and filing Form 3921 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 3921?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS Form 3921 is an information return used to report the
                  transfer of stock to an employee when they exercise{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Incentive Stock Options (ISOs)
                  </Link>
                  , under Section 422(b). If you have issued stock options to
                  employees through your corporation,{' '}
                  <span style={{ fontWeight: 600 }}> Form 3921</span> is
                  required to report important details about the stock transfer
                  such as the transfer date, the amount paid by the employee,
                  and the stock’s fair market value (FMV) at the time of
                  exercise.
                </Typography>

                <Typography variant="body1" id="dates" sx={{ mb: 4 }}>
                  As a <span style={{ fontWeight: 600 }}>payer</span>, it is
                  essential to file Form 3921 with the IRS and provide a copy to
                  the employee to ensure accurate and timely tax reporting.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates to file Form 3921
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  Businesses that transfer stock to employees through an
                  Incentive Stock Option (ISO) plan must comply with IRS
                  reporting requirements, including meeting the Form 3921
                  deadline.
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  The deadline for the 2025 tax year is:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Sending employee copy
                          </TableCell>

                          <TableCell sx={{}}>February 2 , 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS e-filing
                          </TableCell>

                          <TableCell sx={{}}>March 31, 2026 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        File Your Form 3921 on time!
                      </Typography>
                      <Typography variant="body2">
                        With Taxzerone file your Form 3921 effortlessly{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3921duedates`}
                        id="pen"
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties
                </Typography>

                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Scenario
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalty per return
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalty for small businesses
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Penalty for large businesses
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Failure to file on time
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $60
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $239,000
                          </TableCell>

                          <TableCell sx={{}}>$683,000 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            after 30 days before August 1
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $130
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $683,000
                          </TableCell>

                          <TableCell sx={{}}>$2,049,000 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            after August 1 or not filed
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $340
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $1,366,000
                          </TableCell>

                          <TableCell sx={{}}>$4,098,500 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Intentional Disregard
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $680
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            No limit
                          </TableCell>

                          <TableCell id="filing" sx={{}}>
                            No limit{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Advantages and Filing Form 3921 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/information-returns/efile-irs-form-3921/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Filing Form 3921
                  </Link>{' '}
                  with TaxZerone gives you a smooth, efficient experience from
                  start to <br />
                  finish — with guided steps, built-in error checks, and fast,
                  secure e-filing to the IRS.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone offers solutions to send employee copies through
                  both e-delivery and postal mail and also allows you to
                  download Form 3921 directly from the IRS.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  File your IRS form 3921 easily in 3 simple steps with
                  TaxZerone.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Step 1:</span> Login to your{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3921duedates`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    TaxZerone Account
                  </Link>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Step 2:</span> Choose{' '}
                  &quot;Form 3921&quot; and enter the required details of
                  corporations and employee details including Stock option
                  exercised details.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>Step 3:</span> Transmit Form
                  3921 to the IRS and issue an Employee’s copy via ZeroneVault
                  or postal mailing.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on IRS guidelines.
                        For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i3921.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form 3921
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Get Started with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              File Form 3921 effortlessly and send employee copies with
              ZeroneVault.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3921duedates`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form3921DueDates;
